import {
    MY_FILTER_VALUES,
    MY_SORTING_VALUES,
    QUALITY_VALUES,
    SORTING_VALUES,
    WISHLIST_SORTING_VALUES,
} from 'mk/bazaar/common/enums';

import {
    DEVICE_CHANGED,
    DeviceChangedAction,
    SOFT_KEYBOARD_TOGGLE,
    SoftKeyboardToggleAction,
    VIEWPORT_CHANGED,
    ViewportChangedAction,
} from 'mk2/actions';
import {
    MY_BAZAAR_CLEAR_LAST_CREATED_PRODUCT,
    MyBazaarClearLastCreatedProductAction,
} from 'mk2/apps/bazaar/containers/MyBazaar/MyBazaar.actions';
import {
    PRIVACY_LEAFLET_ACCEPT_SUCCESS,
    PrivacyLeafletAcceptSuccessAction,
} from 'mk2/containers/PrivacyReminder/PrivacyReminder.actions';
import {
    WEBPUSH_NOTIFICATIONS_DISABLE,
    WEBPUSH_NOTIFICATIONS_SUBSCRIBED,
    WebpushNotificationsDisableAction,
    WebpushNotificationsSubscribedAction,
} from 'mk2/containers/WebpushNotifications/WebpushNotifications.actions';
import { UserAgentParsed } from 'mk2/helpers/detects';
import { Plan, UserEntity } from 'mk2/schemas';
import { Reducer } from 'redux';

interface KeyboardState {
    opened: boolean;
    height: number;
}

interface ViewportState {
    width?: number;
    height?: number;
}

export interface RequestDeviceState {
    mobile: boolean;
    desktop: boolean;
    keyboard: KeyboardState;
    screen: {
        width?: number;
        height?: number;
    };
    viewport: ViewportState;
}

export type Quality = keyof typeof QUALITY_VALUES;
export type GridSort = keyof typeof SORTING_VALUES;
export type MyGridSort = keyof typeof MY_SORTING_VALUES;
export type MyGridFilter = keyof typeof MY_FILTER_VALUES;
export type WishlistSort = keyof typeof WISHLIST_SORTING_VALUES;

export interface Settings {
    cleanSort?: boolean;
    quality?: Quality[];
    gridSort?: GridSort;
    myGridSort?: MyGridSort;
    myGridFilter?: MyGridFilter;
    wishlistSort?: WishlistSort;
    buySort?: 'new' | 'time';
    sendBazaarEmails?: boolean;
    afterCreateShareModalDisabled?: boolean;
}

export interface RequestStateProvidedFromServer {
    baseUrl: string;
    user: UserEntity;
    permissions: string[];
    settings: Settings;
    userAgent: UserAgentParsed;
    phoneVerificationMode: 'newuser' | 'forced' | null;
    privacyLeaflet: boolean;
    webpush: string;
}

export interface RequestState extends RequestStateProvidedFromServer {
    device: RequestDeviceState;
    lastKnownUsername: string;
    pregnancyNewsletterSubscribed: boolean;
    pregnancyNewsletterClosed: boolean;
    newGroupsEnabled: boolean;
}

export const initialSettings: Settings = {
    afterCreateShareModalDisabled: false,
    buySort: 'new',
    cleanSort: false,
    gridSort: 'update',
    myGridFilter: 'all',
    myGridSort: 'new_messages',
    quality: ['old', 'new', 'handmade'],
    sendBazaarEmails: true,
    wishlistSort: 'wishlist',
};

export const initialState: RequestState = {
    baseUrl: '',
    user: {
        id: 0,
        username: 'anonymous',
        avatar: '', // TODO: Add generic avatar
        avatarCampaign: '',
        // county: null,
        activePlan: Plan.FREE,
        gender: 'f',
        phoneVerified: false,
        isBlocked: false,
        likesCount: 0,
        likers: [],
        isUntrustworthy: false,
    },
    permissions: [],
    settings: initialSettings,
    userAgent: null,
    device: {
        // Defaults to mobile
        mobile: true,
        desktop: false,
        keyboard: {
            opened: false,
            height: 200, // Defaults to 200px
        },
        screen: {},
        viewport: {},
    },
    phoneVerificationMode: null,
    lastKnownUsername: null,
    privacyLeaflet: null,
    webpush: null,
    pregnancyNewsletterSubscribed: null,
    pregnancyNewsletterClosed: null,
    newGroupsEnabled: null,
};

type RequestReducerAction =
    | DeviceChangedAction
    | ViewportChangedAction
    | SoftKeyboardToggleAction
    // | NewsletterModalCloseAction
    | PrivacyLeafletAcceptSuccessAction
    | WebpushNotificationsSubscribedAction
    | WebpushNotificationsDisableAction
    | MyBazaarClearLastCreatedProductAction;

const request: Reducer<RequestState> = (state = initialState, action: RequestReducerAction) => {
    switch (action.type) {
        case DEVICE_CHANGED:
            return {
                ...state,
                device: {
                    ...state.device,
                    mobile: action.mobile,
                    desktop: action.desktop,
                },
            };
        case VIEWPORT_CHANGED:
            return {
                ...state,
                device: {
                    ...state.device,
                    viewport: {
                        width: action.width,
                        height: action.height,
                    },
                },
            };
        case SOFT_KEYBOARD_TOGGLE:
            return {
                ...state,
                device: {
                    ...state.device,
                    keyboard: {
                        opened: action.opened,
                        height: action.height,
                    },
                },
            };
        // case NEWSLETTER_MODAL_CLOSE:
        //     return {
        //         ...state,
        //         pregnancyNewsletterClosed: true,
        //     };
        case PRIVACY_LEAFLET_ACCEPT_SUCCESS:
            return {
                ...state,
                privacyLeaflet: true,
            };
        case WEBPUSH_NOTIFICATIONS_SUBSCRIBED:
            return {
                ...state,
                webpush: action.token,
            };
        case WEBPUSH_NOTIFICATIONS_DISABLE:
            return {
                ...state,
                webpush: 'disabled',
            };
        case MY_BAZAAR_CLEAR_LAST_CREATED_PRODUCT:
            if (action.disable) {
                return {
                    ...state,
                    settings: {
                        ...state.settings,
                        afterCreateShareModalDisabled: true,
                    },
                };
            } else {
                return state;
            }
        default:
            return state;
    }
};

export default request;

export const getBaseUrl = (state: RequestState): string => state.baseUrl;
export const getUser = (state: RequestState): UserEntity => state.user;
export const getPermissions = (state: RequestState): string[] => state.permissions;
export const getSettings = (state: RequestState): Settings => state.settings;
export const getDevice = (state: RequestState): RequestDeviceState => state.device;
export const getDeviceViewport = (state: RequestState): ViewportState => state.device.viewport;
export const getDeviceMobile = (state: RequestState): boolean => state.device.mobile;
export const getDeviceDesktop = (state: RequestState): boolean => state.device.desktop;
export const getDeviceKeyboard = (state: RequestState): KeyboardState => state.device.keyboard;
export const getPhoneVerificationMode = (state: RequestState) => state.phoneVerificationMode;
export const getPrivacyLeaflet = (state: RequestState): boolean => state.privacyLeaflet;
export const getPregnancyNewsletterSubscribed = (state: RequestState): boolean => state.pregnancyNewsletterSubscribed;
export const getPregnancyNewsletterClosed = (state: RequestState): boolean => state.pregnancyNewsletterClosed;
export const getNewGroupsEnabled = (state: RequestState): boolean => state.newGroupsEnabled;
export const getWebpush = (state: RequestState): string => state.webpush;
